import { useCallback, useState } from 'react';
import { UserResource } from '@clerk/types';
import { useSnackbar } from '@ghosts-inc/design-system/hooks';
import { acknowledgeTermsAndConditions } from '../actions';

type Props = {
  user: UserResource | null | undefined;
  onSuccessCallback?: () => void;
};

export const useAcknowledgeTermsAndConditions = ({ user, onSuccessCallback }: Props) => {
  const { showSnackbar } = useSnackbar();
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setIsAcknowledged(event.target.checked);
  };

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onSubmit = async (): Promise<void> => {
    // The button should be disabled when `isAcknowledged` is false but
    // keeping this check as a safeguard.
    if (!isAcknowledged) {
      return;
    }

    setIsLoading(true);
    const [_, error] = await acknowledgeTermsAndConditions();

    if (error) {
      showSnackbar({
        severity: 'alertError',
        title: 'An error occurred',
        text: error,
      });
      setIsLoading(false);
      return;
    }

    await user?.reload();

    // we don't need to set `isLoading` to false here because the `onSuccessCallback` will
    // redirect the user to the home page.
    onClose();
    onSuccessCallback?.();
  };

  return { isAcknowledged, isOpen, isLoading, onOpen, onSubmit, onClose, handleCheckboxChange };
};
